/* eslint-disable react-hooks/exhaustive-deps */
import classNames from 'classnames';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';

import { FiChevronRight } from 'react-icons/fi';

import Empty from 'components/common/empty/Empty';

import { SELECT_CATEGORY_ADD, SELECT_CATEGORY_VIEW } from 'constants/skills';

import { getFilterSkills } from 'utils/skillV2';

import AddSkillModal from 'pages/settings/skills/components/AddSkillModal';
import SkillCategoryChild from './SkillCategoryChild';
import AddSkillCategoryModal from 'pages/settings/skills/components/AddSkillCategory';

import { SkillNode } from 'interface/skillTree';
import type { GlobalRatingItem } from 'pages/settings/skills/types';

interface SkillCategoryModalNode {
  id: number;
  name: string;
  description?: string;
  subSkills?: Array<{ id?: number | string }>;
}

interface SkillModalNode {
  id?: number;
  name?: string;
  description?: string;
  isEndSkill?: boolean;
  localRating?: Array<{
    rating: number;
    description: string;
  }>;
}

interface SkillParentNode {
  id: number;
  name: string;
}

const toNumericId = (id: number | string): number => Number(id);

const toSkillCategoryModalNode = (skill: SkillNode): SkillCategoryModalNode => ({
  id: toNumericId(skill.id),
  name: skill.name || '',
  description: skill.description,
  subSkills: skill.subSkills,
});

const toSkillModalNode = (skill: SkillNode): SkillModalNode => ({
  id: skill.id !== undefined ? toNumericId(skill.id) : undefined,
  name: skill.name,
  description: skill.description,
  isEndSkill: (skill as SkillNode & { isEndSkill?: boolean }).isEndSkill,
  localRating: (skill as SkillNode & { localRating?: SkillModalNode['localRating'] }).localRating,
});

const toSkillParentNode = (skill: SkillNode): SkillParentNode => ({
  id: toNumericId(skill.id),
  name: skill.name || '',
});

const toSkillParentNodes = (skills: SkillNode[]): SkillParentNode[] => skills.map(toSkillParentNode);

interface Props {
  skills: SkillNode[];
  globalRatings?: GlobalRatingItem[];
  showAddSkills?: boolean;
  refechSkills: () => void;
  selectedSkills?: SkillNode[] | null;
  onSelectedSkillsChange?: (skills: SkillNode[]) => void;
  searchSkillTree?: SkillNode[];
  setSelectedSearchSkill: (skill: SkillNode | null) => void;
  isCategoryModalOpen: boolean;
  setIsCategoryModalOpen: (open: boolean) => void;
}

const SkillSetV2: React.FC<Props> = (props) => {
  const {
    skills,
    globalRatings = [],
    showAddSkills = true,
    refechSkills,
    selectedSkills,
    onSelectedSkillsChange,
    searchSkillTree,
    setSelectedSearchSkill,
    isCategoryModalOpen,
    setIsCategoryModalOpen,
  } = props;

  const selectedCategoryRef = useRef<HTMLButtonElement | null>(null);

  const [selectedCategory, setSelectedCategory] = useState<SkillNode | null>(null);

  const [selectedCategoryModalData, setSelectedCategoryModalData] = useState<SkillCategoryModalNode | null>(null);

  const [isSkillModalOpen, setIsSkillModalOpen] = useState(false);
  const [skillData, setSkillData] = useState<SkillModalNode | null>(null);
  const [skillParentData, setSkillParentData] = useState<SkillParentNode[] | null>(null);

  const filterSkills = useMemo(() => getFilterSkills(skills, searchSkillTree), [searchSkillTree, skills]);

  const searchSelectedCategory = searchSkillTree?.[0] || null;
  const selectedCategoryId = searchSelectedCategory?.id ?? selectedCategory?.id;
  const resolvedSelectedCategory = selectedCategoryId
    ? filterSkills.find((skill) => skill.id === selectedCategoryId) || null
    : null;

  useEffect(() => {
    if (selectedCategoryRef.current) {
      selectedCategoryRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [resolvedSelectedCategory]);

  const openCategoryModal = useCallback(() => {
    setIsCategoryModalOpen(true);
  }, [setIsCategoryModalOpen]);

  const closeCategoryModal = () => {
    setIsCategoryModalOpen(false);
    setSelectedCategoryModalData(null);
  };

  const openEditCategoryModal = (categoryData: SkillNode) => {
    setSelectedCategoryModalData(toSkillCategoryModalNode(categoryData));
    openCategoryModal();
  };

  const openSkillModal = () => {
    setIsSkillModalOpen(true);
  };

  const closeSkillModal = () => {
    setIsSkillModalOpen(false);
    setSkillData(null);
    setSkillParentData(null);
  };

  const openAddSkillModal = (parentData: SkillNode[]) => {
    setSkillParentData(toSkillParentNodes(parentData));
    openSkillModal();
  };

  const openEditSkillModal = (editSkillData: SkillNode, parentData: SkillNode[]) => {
    setSkillData(toSkillModalNode(editSkillData));
    setSkillParentData(toSkillParentNodes(parentData));
    openSkillModal();
  };

  const addSubSkill = (newParentData: SkillParentNode[]) => {
    setSkillData(null);
    setSkillParentData(newParentData);
  };

  const handleCategoryClick = (category: SkillNode) => {
    if (resolvedSelectedCategory?.id !== category.id) {
      setSelectedCategory(category);
      setSelectedSearchSkill(null);
    }
  };

  return (
    <div className="skill-set-area">
      <AddSkillCategoryModal
        isOpen={isCategoryModalOpen}
        onClose={closeCategoryModal}
        categoryData={selectedCategoryModalData}
        callback={refechSkills}
      />

      <AddSkillModal
        isOpen={isSkillModalOpen}
        skillData={skillData}
        skillParentData={skillParentData}
        onClose={closeSkillModal}
        addSubSkill={addSubSkill}
        callback={refechSkills}
      />

      <div className="skill-set-area__container">
        <div className="area-list-container">
          {filterSkills?.map((item) => {
            const selected = item.id === resolvedSelectedCategory?.id;

            return (
              <button
                key={item.id}
                onClick={() => handleCategoryClick(item)}
                className={classNames('area-list-container__item', {
                  'area-list-container__item--active': selected,
                })}
                ref={selected ? selectedCategoryRef : null}
              >
                <div className="d-flex align-items-center gap-px-8">
                  <div className="name">{item.name}</div>
                  <div className="count">{item.totalSkills}</div>
                </div>
                <div className="mt-5">
                  <FiChevronRight />
                </div>
              </button>
            );
          })}
        </div>

        <div className="skills-list-container">
          {!resolvedSelectedCategory?.id && (
            <div className="skills-list-container__empty">
              <Empty message={selectedSkills ? SELECT_CATEGORY_ADD : SELECT_CATEGORY_VIEW} />
            </div>
          )}

          {resolvedSelectedCategory && (
            <SkillCategoryChild
              showAddSkills={showAddSkills}
              openEditCategoryModal={openEditCategoryModal}
              openEditSkillModal={openEditSkillModal}
              openAddSkillModal={openAddSkillModal}
              selectedCategory={resolvedSelectedCategory}
              globalRatings={globalRatings}
              selectedSkills={selectedSkills}
              onSelectedSkillsChange={onSelectedSkillsChange}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SkillSetV2;
